import * as React from 'react';
import { useParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { EmojiInsertButton } from '@/components/EmojiInsertButton';
import { MentionButton, MentionSuggest } from '@/components/MentionSuggest';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * Text that ends up in Discord: buttons that insert custom emoji codes and
   * mentions, plus @-suggestions while typing (on server pages).
   */
  emoji?: boolean;
  /** Server for the @-suggestions on pages outside /server/:guildId (ticket view). */
  mentionGuildId?: string;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, emoji, mentionGuildId, ...props }, ref) => {
    const innerRef = React.useRef<HTMLTextAreaElement>(null);
    React.useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);
    const params = useParams<{ guildId: string }>();
    const guildId = mentionGuildId ?? params.guildId;
    const mentions = emoji && !!guildId;

    const field = (
      <textarea
        className={cn(
          'flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          emoji && 'pr-10',
          className
        )}
        ref={innerRef}
        {...props}
      />
    );
    if (!emoji) return field;
    return (
      <div className="relative">
        {field}
        <EmojiInsertButton
          target={innerRef}
          disabled={props.disabled}
          className="absolute right-1.5 top-1.5"
        />
        {mentions && (
          <>
            <MentionButton target={innerRef} disabled={props.disabled} className="absolute right-1.5 top-9" />
            <MentionSuggest target={innerRef} guildId={guildId!} />
          </>
        )}
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';
